import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import WysiwygEditor from "../../../components/WysiwygEditor";
import feedbackApi from "../api/feedbackApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import {
  FEEDBACK_SENTIMENTS,
  PERFORMANCE_QUERY_KEYS,
} from "../constants/performanceConstants";
import performanceRoutes from "../routes/performanceRoutes";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";
import useFeedbackTypeOptions from "../hooks/useFeedbackTypeOptions";

const FeedbackPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const { feedbackTypeOptions, isLoading: feedbackTypesLoading } =
    useFeedbackTypeOptions();
  const [showGuide, setShowGuide] = React.useState(false);
  const [toEmployeeId, setToEmployeeId] = React.useState("");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadFeedback = can(PERFORMANCE_PERMISSIONS.FEEDBACK_READ);
  const canCreateFeedback = can(PERFORMANCE_PERMISSIONS.FEEDBACK_CREATE);

  if (!canReadFeedback) {
    return (
      <ModulePageShell
        title="Give Feedback"
        description="Create and review continuous feedback entries."
      >
        <EmptyState message="You do not have permission to view feedback." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.feedback, "given", toEmployeeId],
    queryFn: () =>
      feedbackApi.listFeedback({
        toEmployeeId: toEmployeeId || undefined,
        page: 1,
        limit: 50,
      }),
  });

  const createMutation = useMutation({
    mutationFn: feedbackApi.createFeedback,
    onSuccess: () => {
      toast({ title: "Feedback submitted", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.feedback],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to submit feedback",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { control, register, handleSubmit, reset } = useForm({
    defaultValues: {
      fromEmployeeId: "",
      toEmployeeId: "",
      feedbackTypeId: "",
      title: "",
      message: "",
      sentiment: "NEUTRAL",
      isAnonymous: false,
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Give Feedback"
      description="Create and review continuous feedback entries."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canCreateFeedback ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) => createMutation.mutate(values))}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Submit Feedback</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>From Employee ID</FormLabel>
                  <Select
                    placeholder="Select employee"
                    {...register("fromEmployeeId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>To Employee ID</FormLabel>
                  <Select
                    placeholder="Select employee"
                    {...register("toEmployeeId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Feedback Type</FormLabel>
                  <Select
                    placeholder="Select feedback type"
                    {...register("feedbackTypeId")}
                    isDisabled={feedbackTypesLoading}
                  >
                    {feedbackTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input {...register("title")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Sentiment</FormLabel>
                  <Select {...register("sentiment")}>
                    {FEEDBACK_SENTIMENTS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <FormControl isRequired>
                <FormLabel>Message</FormLabel>
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <WysiwygEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Write feedback details, context, and suggestions"
                    />
                  )}
                />
              </FormControl>
              <Checkbox {...register("isAnonymous")}>
                Anonymous feedback
              </Checkbox>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Submit
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to feedback, but cannot create feedback." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="semibold">Given Feedback</Text>
          <Select
            maxW="280px"
            placeholder="Filter by receiver employee"
            value={toEmployeeId}
            onChange={(e) => setToEmployeeId(e.target.value)}
            isDisabled={usersLoading}
          >
            {userOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </HStack>

        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No feedback available." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Type</Th>
                <Th>Date</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>
                    {item.fromEmployeeId?.name || item.fromEmployeeId || "-"}
                  </Td>
                  <Td>{item.toEmployeeId?.name || item.toEmployeeId || "-"}</Td>
                  <Td>
                    {item.feedbackTypeId?.name || item.feedbackTypeId || "-"}
                  </Td>
                  <Td>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    {item.acknowledgement?.acknowledged
                      ? "ACKNOWLEDGED"
                      : "NEW"}
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        navigate(performanceRoutes.feedbackDetail(item._id))
                      }
                    >
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default FeedbackPage;
